import React, { useState } from "react";
import * as XLSX from "xlsx";
import { useGetStoreSalesQuery } from "../slices/storeSalesApiSlice";
import { useGetStoreExpensesQuery } from "../slices/storeExpensesApiSlice";
import { useGetStoreIncomesQuery } from "../slices/storeIncomesApiSlice";
import { useGetDepositsQuery } from "../slices/depositsApiSlice";
import { useGetStoreSuppliesQuery } from "../slices/storeSuppliesApiSlice";
import { Link} from 'react-router-dom';

export default function BalanceByUserStaff() {
  const today = new Date().toISOString().split("T")[0];
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [summaryByDate, setSummaryByDate] = useState({});
  const [storeIncomeBreakdownsByDate, setStoreIncomeBreakdownsByDate] = useState({});

  const { data: storeSalesData } = useGetStoreSalesQuery();
  const { data: storeExpensesData } = useGetStoreExpensesQuery({ startDate, endDate });
  const { data: storeIncomesData } = useGetStoreIncomesQuery({ startDate, endDate });
  const { data: depositsData } = useGetDepositsQuery({ startDate, endDate });
  const { data: storeSuppliesData } = useGetStoreSuppliesQuery();

  const getDateRange = (start, end) => {
    const dates = [];
    let current = new Date(start);
    const last = new Date(end);
    while (current <= last) {
      dates.push(current.toISOString().split("T")[0]);
      current.setDate(current.getDate() + 1);
    }
    return dates;
  };

  const filterByDate = (data, date) =>
    data?.filter(item => item.date?.startsWith(date)) || [];

  const groupByUsername = (items, key) => {
    return items.reduce((acc, item) => {
      const username = item.username || "Unknown";
      acc[username] = (acc[username] || 0) + Number(item[key] || 0);
      return acc;
    }, {});
  };

  const groupStoreIncomeByCategory = (items) => {
    const grouped = { Morning: 0, Afternoon: 0, Night: 0 };
    items.forEach(item => {
      const category = item.category;
      const amount = Number(item.amount || 0);
      if (grouped.hasOwnProperty(category)) {
        grouped[category] += amount;
      }
    });
    return grouped;
  };

  const calculateSummary = () => {
    const dates = getDateRange(startDate, endDate);
    const summary = {};
    const breakdowns = {};

    dates.forEach(date => {
      const storeSales = groupByUsername(filterByDate(storeSalesData?.storeSales, date), "totalPrice");
      const storeExpenses = groupByUsername(filterByDate(storeExpensesData?.storeExpenses, date), "amount");
      const storeIncomes = filterByDate(storeIncomesData?.storeIncomes, date);
      const storeIncomeTotals = groupByUsername(storeIncomes, "amount");
      const deposits = groupByUsername(filterByDate(depositsData?.deposits, date), "amount");
      const storeSupplies = groupByUsername(filterByDate(storeSuppliesData?.storeSupplies, date), "cardCost");

      breakdowns[date] = groupStoreIncomeByCategory(storeIncomes);

      const allUsernames = new Set([
        ...Object.keys(storeSales),
        ...Object.keys(storeExpenses),
        ...Object.keys(storeIncomeTotals),
        ...Object.keys(deposits),
        ...Object.keys(storeSupplies),
      ]);

      const rows = Array.from(allUsernames).map(username => ({
        username,
        storeSales: storeSales[username] || 0,
        storeExpenses: storeExpenses[username] || 0,
        storeIncomes: storeIncomeTotals[username] || 0,
        deposits: deposits[username] || 0,
        storeSupplies: storeSupplies[username] || 0,
      }));

      const totals = rows.reduce(
        (acc, row) => {
          acc.storeSales += row.storeSales;
          acc.storeExpenses += row.storeExpenses;
          acc.storeIncomes += row.storeIncomes;
          acc.deposits += row.deposits;
          acc.storeSupplies += row.storeSupplies;
          acc.balance += row.storeIncomes - (row.storeExpenses + row.storeSales) ;
          
          return acc;
        },
        { storeSales: 0, storeExpenses: 0, storeIncomes: 0, deposits: 0, storeSupplies: 0, balance: 0 }
      );

      summary[date] = { rows, totals };
    });

    setSummaryByDate(summary);
    setStoreIncomeBreakdownsByDate(breakdowns);
  };

  const exportToExcel = () => {
    const workbook = XLSX.utils.book_new();

    Object.entries(summaryByDate).forEach(([date, { rows, totals }]) => {
      const sheetData = [
        ...rows.map(row => ({
          Date: date,
          User: row.username,
          StoreSales: row.storeSales.toFixed(2),
          StoreExpenses: row.storeExpenses.toFixed(2),
          StoreIncomes: row.storeIncomes.toFixed(2),
          Deposits: row.deposits.toFixed(2),
          StoreSupplies: row.storeSupplies.toFixed(2),
        })),
        {
          Date: date,
          User: "TOTAL",
          StoreSales: totals.storeSales.toFixed(2),
          StoreExpenses: totals.storeExpenses.toFixed(2),
          StoreIncomes: totals.storeIncomes.toFixed(2),
          Deposits: totals.deposits.toFixed(2),
          StoreSupplies: totals.storeSupplies.toFixed(2),
        },
      ];
      const worksheet = XLSX.utils.json_to_sheet(sheetData);
      XLSX.utils.book_append_sheet(workbook, worksheet, date);
    });

    Object.entries(storeIncomeBreakdownsByDate).forEach(([date, breakdown]) => {
      const sheetData = Object.entries(breakdown).map(([category, amount]) => ({
        Category: category,
        Amount: amount.toFixed(2),
      }));
      const worksheet = XLSX.utils.json_to_sheet(sheetData);
      XLSX.utils.book_append_sheet(workbook, worksheet, `${date}_StoreIncomeBreakdown`);
    });

    XLSX.writeFile(workbook, `Summary_${startDate}_to_${endDate}.xlsx`);
  };

  return (
    <div className="range-user-summary">
      <h2>Summary by User and Date</h2>
      <div className="date-range">
        <label>
          Start Date:
          <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} />
        </label>
        <label>
          End Date:
          <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} />
        </label>
      </div>
      <div className="actions">
        <button onClick={calculateSummary}>Generate Summary</button>
        <button onClick={exportToExcel} disabled={Object.keys(summaryByDate).length === 0} style={{ display: 'none' }}>
          Export to Excel
        </button>
        <Link to='/balancebycustomer' className='btn btn-light my-3' style={{ display: 'none' }} >
            Sum By Customer
        </Link>
          
      </div>

      {Object.entries(summaryByDate).map(([date, { rows, totals }]) => (
        <div key={date} className="daily-summary">
          <h3>{date}</h3>
          <table>
            <thead>
              <tr>
                <th>User</th>
                <th>StoreSales</th>
                <th>Inc/Exp</th>
                <th>NetCash</th>
                <th>CashCollected</th>
                <th >Balance</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(row => (
                <tr key={row.username}>
                  <td>{row.username}</td>
                  <td>{row.storeSales.toFixed(2)}</td>
                  <td>{row.storeExpenses.toFixed(2)}</td>
                  <td >{(row.storeSales + row.storeExpenses ).toFixed(2)}</td>
                  <td >{row.storeIncomes.toFixed(2)}</td>
                  <td style={{ display: 'none' }}>{row.deposits.toFixed(2)}</td>
                  <td style={{ display: 'none' }}>{row.storeSupplies.toFixed(2)}</td>
                  <td >{(row.storeSales - row.storeIncomes  + row.storeExpenses ).toFixed(2)}</td>
                </tr>
              ))}
              <tr className="totals-row">
                <td><strong>Total</strong></td>
                <td>{totals.storeSales.toFixed(2)}</td>
                <td>{totals.storeExpenses.toFixed(2)}</td>
                <td>{(totals.storeSales + totals.storeExpenses).toFixed(2)}</td>
                <td>{totals.storeIncomes.toFixed(2)}</td>
                <td style={{ display: 'none' }}>{totals.deposits.toFixed(2)}</td>
                <td style={{ display: 'none' }}>{totals.storeSupplies.toFixed(2)}</td>
                <td >{totals.balance.toFixed(2)}</td>
                
              </tr>
            </tbody>
          </table>

          <h4>StoreIncome Breakdown</h4>
<table>
  <thead>
    <tr>
      <th>Category</th>
      <th>Amount</th>
    </tr>
  </thead>
  <tbody>
    {Object.entries(storeIncomeBreakdownsByDate[date] || {}).map(([category, amount]) => (
      <tr key={category}>
        <td>{category}</td>
        <td>{amount.toFixed(2)}</td>
      </tr>
    ))}
    <tr className="totals-row">
      <td><strong>Total</strong></td>
      <td>
        {Object.values(storeIncomeBreakdownsByDate[date] || {})
          .reduce((sum, val) => sum + val, 0)
          .toFixed(2)}
      </td>
    </tr>
  </tbody>
</table>
        </div>
      ))}
    </div>
  );
}